import keyBy from 'lodash/keyBy';

import {lookupTablesClient} from 'common/report/queries/app-api/lookup-tables-client';
import {LookupTable} from 'common/widgets/query-block/properties-menu/types';
import {ResourceType} from 'common/report/constants';
import {PropertyType} from 'common/widgets/property-filter-menu/models/types';

import {eventPropertiesClient} from '../queries/query-api/event-properties-client';
import {Store} from './store';

type LookupTableProperty = {name: string; label: string; type: PropertyType; count: number};
type AugmentedLookupTable = LookupTable & {
  tempMappings: {event: Set<string>; user: Set<string>};
  properties: LookupTableProperty[];
};
interface LookupTables {
  [id: string]: AugmentedLookupTable;
}

const lookupTableProperties = new Map<string, LookupTableProperty[]>();

export const lookupTablesStore = new Store<LookupTables>({
  apiClient: lookupTablesClient,
  defaultValue: {},
  name: `lookup-tables`,
  transform: {
    index: (response) => {
      const tables = response.map((table) => {
        return {
          ...table,
          tempMappings: {event: new Set(), user: new Set()},
          properties: lookupTableProperties.get(table.id) || [],
        };
      });
      return keyBy(tables, `id`);
    },
  },
});

/** Fetch a lookup table's properties and update that table in the store.
    Result is cached and subsequent calls for the same lookup table are aborted */
export async function fetchLookupTableProperties(dataGroupId, projectId, workspaceId) {
  if (lookupTableProperties.get(dataGroupId)) {
    return;
  }
  lookupTableProperties.set(dataGroupId, []);
  const params = {
    dataGroupId,
    includeDimensionProperties: true,
    projectId,
    workspaceId,
  };
  try {
    const properties = await eventPropertiesClient.query(params);
    lookupTableProperties.set(dataGroupId, properties);
    lookupTablesStore.update({
      [dataGroupId]: {
        ...lookupTablesStore.value[dataGroupId],
        properties,
      },
    });
  } catch (error) {
    console.error(`Error loading lookup table properties for dataGroupId ${dataGroupId}:`, error);
  }
}

export function registerTempLookupTableMapping(
  resourceType: ResourceType,
  value: string,
  dataGroupId: string,
): boolean {
  if (getTempLookupTableMappings(resourceType).has(value)) {
    return false;
  }
  const table = lookupTablesStore.value[dataGroupId];
  if (!table) {
    console.error(`Cannot add temporary mapping: lookup table ${dataGroupId} not available in store`);
    return false;
  }

  const tempMappings = {...table.tempMappings, [resourceType]: new Set([...table.tempMappings[resourceType], value])};
  lookupTablesStore.update({
    [dataGroupId]: {
      ...table,
      tempMappings,
    },
  });
  return true;
}

export function unregisterTempLookupTableMapping(resourceType: ResourceType, value: string) {
  const dataGroupId = getTempLookupTableMappings(resourceType).get(value);
  if (!dataGroupId) {
    console.error(`Cannot find temporary mapping to remove for ${resourceType} property '${value}'`);
    return false;
  }
  const table = lookupTablesStore.value[dataGroupId];
  const newMappedProperties = [...table.tempMappings[resourceType]].filter((prop) => prop !== value);
  const tempMappings = {...table.tempMappings, [resourceType]: new Set(newMappedProperties)};
  lookupTablesStore.update({
    [dataGroupId]: {
      ...table,
      tempMappings,
    },
  });
  return true;
}

/** returns a Map of propertyName -> dataGroupId mappings. a property can currently only be mapped to a single data group id */
export function getTempLookupTableMappings(resourceType: ResourceType) {
  if (![ResourceType.Event, ResourceType.User].includes(resourceType)) {
    return new Map();
  }
  const mappings: Map<string, string> = new Map();
  for (const table of Object.values(lookupTablesStore.value)) {
    for (const value of table.tempMappings[resourceType]) {
      mappings.set(value, table.id);
    }
  }
  return mappings;
}
